import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuditActionName, AuditDetail, AuditMeta, AuditPage, AuditQuery, AuditRow, AuditStats } from "../types";
import { auditService } from "../services/dataService";
import { usePermissions } from "../context/PermissionContext";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import CopyIdButton from "../components/CopyIdButton";
import {
  ACTION_CLASS, ACTION_LABEL, ACTOR_KIND_LABEL, AREA_LABEL, DATA_KEY_LABEL, REASON_LABEL,
  describeValue, fieldLabel, fmtLocal, hueOf, initials, relTime,
} from "../utils/auditLabels";
import "./AuditTrail.css";

/**
 * Denetim İzi — kim, ne zaman, nereden, neyi değiştirdi. Salt okunur.
 * Liste sunucuda süzülür (imleçli sayfalama); ayrıntı çekmecesi alan bazında eski → yeni gösterir,
 * aynı istekte değişen diğer kayıtları ve tek bir kaydın tüm geçmişini açar.
 * Adres çubuğu sözleşmesi: ?from&to&action&area&type&actor&q&fail&size&id=<kayıt no>&entity=<Tür>:<kimlik>
 */

type Preset = "today" | "yesterday" | "7" | "30" | "90" | "custom";
type DrawerState = { mode: "detail"; id: number } | { mode: "history"; type: string; id: string; label?: string | null };

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const presetRange = (p: Preset): [string, string] => {
  switch (p) {
    case "today": return [daysAgo(0), daysAgo(0)];
    case "yesterday": return [daysAgo(1), daysAgo(1)];
    case "30": return [daysAgo(29), daysAgo(0)];
    case "90": return [daysAgo(89), daysAgo(0)];
    default: return [daysAgo(6), daysAgo(0)];
  }
};
const presetOf = (from: string, to: string): Preset => {
  for (const p of ["today", "yesterday", "7", "30", "90"] as Preset[]) { const [f, t] = presetRange(p); if (f === from && t === to) return p; }
  return "custom";
};
const fmtDay = (s: string) => fmtLocal(s.slice(0, 10));
const errorText = (error: any, fallback: string) => { const d = error?.response?.data; if (typeof d === "string" && d.length < 300) return d; return d?.detail || d?.title || d?.message || fallback; };
const nf = (n: number) => n.toLocaleString("tr-TR");

/** Değeri hiçbir zaman yazılmayan (ya da kısmen yazılan) alanlar — listede kilit simgesiyle. */
const MASKED_FIELDS = new Set(["PasswordHash", "NationalId", "HealthNotes", "QrToken", "KioskPinHash", "DriverTckn", "TCKN", "Iban", "AccountNo", "KeyHash", "KeyPrefix", "PublicToken", "VerificationToken", "EdmPassword", "EdmSecretKey", "IdempotencyKey", "CreatedFromIp"]);

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10Z" /><path d="M9 9h6M9 12.5h6M9 16h3" /></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>,
  cog: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h9M17 18h3" /><circle cx="15" cy="6" r="2" /><circle cx="9" cy="12" r="2" /><circle cx="15" cy="18" r="2" /></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></svg>,
  left: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 6-6 6 6 6" /></svg>,
  right: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>,
  cpu: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="6" width="12" height="12" rx="2" /><path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" /></svg>,
  device: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></svg>,
  globe: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" /></svg>,
  warn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>,
};

const ACTIONS: [AuditActionName | "", string][] = [["", "Tümü"], ["Insert", "Ekleme"], ["Update", "Değişiklik"], ["Delete", "Silme"], ["SoftDelete", "Pasife alma"], ["Event", "Olay"]];
const PRESETS: [Preset, string][] = [["today", "Bugün"], ["yesterday", "Dün"], ["7", "Son 7 gün"], ["30", "Son 30 gün"], ["90", "90 gün"]];

/** Uzun metin: 160 karakterden sonrası "tamamını göster" ile açılır. */
const Clamp: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  if (text.length <= 160) return <span className="at-clamp">{text}</span>;
  return <span className="at-clamp">{open ? text : `${text.slice(0, 160)}…`}<button type="button" className="at-link at-more" onClick={() => setOpen(!open)}>{open ? "kısalt" : "tamamını göster"}</button></span>;
};

const AuditTrail: React.FC = () => {
  const { has } = usePermissions();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  // ---- süzgeç durumu (adres çubuğundan başlar) ----
  const [range0] = useState(() => presetRange("7"));
  const [from, setFrom] = useState(sp.get("from") || range0[0]);
  const [to, setTo] = useState(sp.get("to") || range0[1]);
  // Seçili hazır aralık ("Son 7 gün" vb.): Yenile'de bugüne göre yeniden hesaplanır; elle tarih seçilince "custom"
  const [presetSel, setPresetSel] = useState<Preset>(() => presetOf(sp.get("from") || range0[0], sp.get("to") || range0[1]));
  const [action, setAction] = useState<AuditActionName | "">((sp.get("action") as AuditActionName) || "");
  const [area, setArea] = useState(sp.get("area") || "");
  const [type, setType] = useState(sp.get("type") || "");
  const [actor, setActor] = useState(sp.get("actor") || "");
  const [failOnly, setFailOnly] = useState(sp.get("fail") === "1");
  const [qInput, setQInput] = useState(sp.get("q") || "");
  const [q, setQ] = useState(sp.get("q") || "");
  const [size, setSize] = useState(() => { const n = Number(sp.get("size")); return [25, 50, 100, 200].includes(n) ? n : 50; });

  const [meta, setMeta] = useState<AuditMeta | null>(null);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [page, setPage] = useState<AuditPage | null>(null);
  const [initial, setInitial] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hoverCid, setHoverCid] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const fail = (message: string) => setAlert({ isOpen: true, message, type: "error" });
  const cardRef = useRef<HTMLElement>(null);

  // ---- çekmece ----
  const [drawer, setDrawer] = useState<DrawerState | null>(() => {
    const id = Number(sp.get("id"));
    if (id > 0) return { mode: "detail", id };
    const ent = sp.get("entity");
    if (ent && ent.includes(":")) { const i = ent.indexOf(":"); return { mode: "history", type: ent.slice(0, i), id: ent.slice(i + 1) }; }
    return null;
  });
  const [stack, setStack] = useState<DrawerState[]>([]);

  // Arama: 350 ms bekletme
  useEffect(() => { const t = window.setTimeout(() => setQ(qInput.trim()), 350); return () => window.clearTimeout(t); }, [qInput]);

  // Adres çubuğu eşlemesi
  useEffect(() => {
    const p = new URLSearchParams();
    const [df, dt] = presetRange("7");
    if (from !== df || to !== dt) { p.set("from", from); p.set("to", to); }
    if (action) p.set("action", action);
    if (area) p.set("area", area);
    if (type) p.set("type", type);
    if (actor) p.set("actor", actor);
    if (failOnly) p.set("fail", "1");
    if (q) p.set("q", q);
    if (size !== 50) p.set("size", String(size));
    if (drawer?.mode === "detail") p.set("id", String(drawer.id));
    if (drawer?.mode === "history") p.set("entity", `${drawer.type}:${drawer.id}`);
    setSp(p, { replace: true });
  }, [from, to, action, area, type, actor, failOnly, q, size, drawer, setSp]);

  const query = useMemo<AuditQuery>(() => ({
    from, to,
    action: action === "SoftDelete" ? "SoftDelete,Restore" : action || undefined,
    area: area || undefined,
    entityType: type || undefined,
    actorUserId: actor && !actor.startsWith("kind:") ? actor : undefined,
    actorKind: actor.startsWith("kind:") ? actor.slice(5) : undefined,
    success: failOnly ? false : undefined,
    q: q || undefined,
    pageSize: size,
  }), [from, to, action, area, type, actor, failOnly, q, size]);

  // İmleç yığını süzgece bağlı: süzgeç değişince kendiliğinden ilk sayfa (çift yükleme yok)
  const qKey = JSON.stringify(query);
  const [pager, setPager] = useState<{ k: string; list: (number | undefined)[] }>({ k: qKey, list: [undefined] });
  const cursors = pager.k === qKey ? pager.list : [undefined];
  const setCursors = (f: (c: (number | undefined)[]) => (number | undefined)[]) => setPager({ k: qKey, list: f(cursors) });
  useEffect(() => { setPager((p) => (p.k === qKey ? p : { k: qKey, list: [undefined] })); }, [qKey]);
  const cursor = cursors[cursors.length - 1];

  // Yalnız EN SON isteğin yanıtı ekrana yazılır (hızlı süzgeç değişiminde eski yanıt yenisini ezmesin)
  const listReq = useRef(0);
  const statsReq = useRef(0);
  const [listError, setListError] = useState<string | null>(null);
  const loadList = useCallback(async () => {
    const my = ++listReq.current;
    setBusy(true);
    try {
      const p = await auditService.list({ ...query, cursor });
      if (my !== listReq.current) return;
      setPage(p); setListError(null);
    } catch (err) {
      if (my !== listReq.current) return;
      const msg = errorText(err, "Denetim izi yüklenemedi.");
      setPage({ items: [], total: 0, nextCursor: null }); // eski süzgecin satırları/imleci ekranda kalmasın
      setListError(msg); fail(msg);
    } finally {
      if (my === listReq.current) { setBusy(false); setInitial(false); }
    }
  }, [query, cursor]);
  useEffect(() => { loadList(); }, [loadList]);

  const loadStats = useCallback(async () => {
    const my = ++statsReq.current;
    try { const s = await auditService.stats(from, to); if (my === statsReq.current) setStats(s); } catch { /* özet yoksa liste yine çalışır */ }
  }, [from, to]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { auditService.meta().then(setMeta).catch(() => setMeta(null)); }, []);

  const refresh = async () => {
    if (presetSel !== "custom") {
      const [f, t] = presetRange(presetSel);
      if (f !== from || t !== to) { setFrom(f); setTo(t); return; } // gün döndü: yeni aralık kendi yüklemesini tetikler
    }
    await Promise.all([loadList(), loadStats()]);
  };

  const actorName = useCallback((id?: string | null) => (id && meta?.actors.find((a) => a.userId === id)?.name) || null, [meta]);
  const typeLabel = useCallback((t: string) => meta?.types.find((x) => x.code === t)?.label || (t.startsWith("bulk.") ? "Toplu işlem" : t), [meta]);
  const typeExists = useCallback((t: string) => !!meta?.types.some((x) => x.code === t), [meta]);

  const setPreset = (p: Preset) => { const [f, t] = presetRange(p); setPresetSel(p); setFrom(f); setTo(t); };
  /** Yazılırken tarayıcı "0002-…", "0020-…" gibi ara değerler gönderir: 2000 öncesi yok sayılır.
   *  Düzenlenen uç korunur; diğer uç gerekirse ona çekilir (takas, kullanıcının girdisini kaybettiriyordu). */
  const validDay = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && v >= "2000-01-01" && v <= daysAgo(0);
  const editFrom = (v: string) => { if (!validDay(v)) return; setPresetSel("custom"); setFrom(v); if (to < v) setTo(v); };
  const editTo = (v: string) => { if (!validDay(v)) return; setPresetSel("custom"); setTo(v); if (from > v) setFrom(v); };
  const preset = presetSel === "custom" ? presetOf(from, to) : presetSel;
  const filterOn = !!(action || area || type || actor || failOnly || q || preset !== "7");
  const clearAll = () => { setAction(""); setArea(""); setType(""); setActor(""); setFailOnly(false); setQInput(""); setQ(""); setPreset("7"); };

  const goPage = (dir: 1 | -1) => {
    if (dir === 1 && page?.nextCursor) setCursors((c) => [...c, page.nextCursor!]);
    if (dir === -1 && cursors.length > 1) setCursors((c) => c.slice(0, -1));
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const blob = await auditService.exportExcel(query);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `denetim-izi-${from}-${to}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
      if ((page?.total ?? 0) > 50000)
        setAlert({ isOpen: true, type: "warning", message: `Süzgeçle ${nf(page!.total)} kayıt eşleşiyor; dosya yalnız en yeni 50.000 kaydı içeriyor. Tamamı için tarih aralığını daraltıp birden çok dosya alın.` });
    } catch (err) { fail(errorText(err, "Excel dosyası oluşturulamadı.")); }
    finally { setExporting(false); }
  };

  // ---- çekmece aç/kapat ----
  const openDetail = (id: number, push = false) => { if (push && drawer) setStack((s) => [...s, drawer]); else if (!push) setStack([]); setDrawer({ mode: "detail", id }); };
  const openHistory = (t: string, id: string, label?: string | null) => { if (drawer) setStack((s) => [...s, drawer]); setDrawer({ mode: "history", type: t, id, label }); };
  const back = () => { setStack((s) => { const prev = s[s.length - 1]; setDrawer(prev || null); return s.slice(0, -1); }); };
  const closeDrawer = () => { setDrawer(null); setStack([]); };

  const rows = useMemo(() => page?.items || [], [page]);
  const currentIdx = drawer?.mode === "detail" ? rows.findIndex((r) => r.id === drawer.id) : -1;
  const step = useCallback((d: 1 | -1) => {
    if (currentIdx < 0) return;
    const n = rows[currentIdx + d];
    if (n) { setStack([]); setDrawer({ mode: "detail", id: n.id }); }
  }, [currentIdx, rows]);

  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (drawer.mode !== "detail" || currentIdx < 0) return;
      if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer, step, currentIdx]);

  if (initial) return <PageLoader message="Denetim izi yükleniyor..." />;

  const st = stats;
  const dailyMax = Math.max(1, ...(st?.daily.map((d) => d.count) || [1]));

  return (
    <div className="at-page">
      <header className="at-head">
        <div>
          <span className="at-kicker">Sistem</span>
          <h1>Denetim İzi</h1>
          <p>Her veri değişikliği, giriş ve önemli işlem kim, ne zaman, nereden ve hangi değerle yapıldığıyla kaydedilir. Kayıtlar salt okunurdur; <b>{st?.retentionDays ?? meta?.retentionDays ?? 90} günden</b> eski kayıtlar her gece 03:30'da otomatik silinir.</p>
        </div>
        <div className="at-head-actions">
          <button className="at-btn" onClick={refresh} disabled={busy}><span className={busy ? "at-spin-icon" : ""}>{I.refresh}</span> Yenile</button>
          <button className="at-btn" onClick={doExport} disabled={exporting || !page?.total}>{exporting ? <span className="at-spin-icon">{I.refresh}</span> : I.download} Excel</button>
          {(has("settings:update") || has("settings:view")) && <button className="at-btn" onClick={() => navigate("/settings?section=audit")}>{I.cog} Saklama ayarı</button>}
        </div>
      </header>

      {st && !st.enabled && (
        <div className="at-banner warn">{I.warn}<span><b>Denetim izi şu an kayıt tutmuyor.</b> Tablo bulunamadı ya da özellik kapalı; veritabanı güncellemesi uygulanana kadar değişiklikler kaydedilmez.</span></div>
      )}
      {st && st.writeFailures > 0 && (
        <div className="at-banner warn">{I.warn}<span>Son başlatmadan bu yana <b>{nf(st.writeFailures)}</b> denetim kaydı yazılamadı; uygulama günlüğünü kontrol edin.</span></div>
      )}

      {st && (
        <section className="at-kpis">
          <div className="at-kpi navy"><span className="l">Bugün</span><span className="v">{nf(st.today)}<small> kayıt</small></span><span className="s">saat 00:00'dan bu yana tüm işlemler</span></div>
          <button className={`at-kpi ${!action && !failOnly ? "on" : ""}`} onClick={() => { setAction(""); setFailOnly(false); }}>
            <span className="l">Seçili aralık</span><span className="v">{nf(st.total)}</span>
            <span className="s"><b>{nf(st.changes)}</b> ekleme/değişiklik · <b>{nf(st.deletes)}</b> silme · <b>{st.distinctActors}</b> kişi</span>
            {st.daily.length > 1 && <span className="at-spark" aria-hidden>{st.daily.slice(-30).map((d) => <i key={d.day} className={d.day.slice(0, 10) === to ? "on" : ""} style={{ height: `${Math.max(8, (d.count / dailyMax) * 100)}%` }} title={`${fmtDay(d.day)}: ${d.count}`} />)}</span>}
          </button>
          <button className={`at-kpi ${st.failedLogins + st.accessDenied > 0 ? "err" : "ok"} ${failOnly ? "on" : ""}`} onClick={() => { setFailOnly(!failOnly); setAction(""); }}>
            <span className="l">Başarısız giriş</span><span className="v">{nf(st.failedLogins)}</span>
            <span className="s"><b>{nf(st.accessDenied)}</b> yetkisiz deneme · <b>{nf(st.failures)}</b> başarısız işlem</span>
          </button>
          <button className={`at-kpi event ${action === "Event" ? "on" : ""}`} onClick={() => setAction(action === "Event" ? "" : "Event")}>
            <span className="l">Olaylar</span><span className="v">{nf(st.events)}</span><span className="s">giriş, şifre, yetki, dışa aktarım, e-belge</span>
          </button>
          <div className="at-kpi ok"><span className="l">Saklama</span><span className="v">{st.retentionDays}<small> gün</small></span>
            <span className="s">en eski {st.oldestRecord ? fmtDay(st.oldestRecord) : "—"} · son temizlik {st.lastPurgeAt ? `${fmtDay(st.lastPurgeAt)}${st.lastPurgeDeleted != null ? ` · ${nf(st.lastPurgeDeleted)} silindi` : ""}` : "henüz yok"}</span>
          </div>
        </section>
      )}

      <section className={`at-card ${busy ? "dim" : ""}`} ref={cardRef}>
        <div className="at-toolbar">
          <div className="at-search">{I.search}<input placeholder="Kayıt adı, e-posta, mesaj, alan adı, kimlik ya da istek no ara" value={qInput} onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setQ(qInput.trim()); }} />{qInput && <button className="at-ib" aria-label="Aramayı temizle" onClick={() => { setQInput(""); setQ(""); }}>{I.close}</button>}</div>
          <div className="at-chipgroup">{PRESETS.map(([p, l]) => <button key={p} className={preset === p ? "on" : ""} onClick={() => setPreset(p)}>{l}</button>)}</div>
          <div className="at-dates">
            <input type="date" aria-label="Başlangıç" value={from} min="2000-01-01" max={daysAgo(0)} onChange={(e) => editFrom(e.target.value)} />
            <span>–</span>
            <input type="date" aria-label="Bitiş" value={to} min="2000-01-01" max={daysAgo(0)} onChange={(e) => editTo(e.target.value)} />
          </div>
        </div>
        <div className="at-toolbar">
          <div className="at-chipgroup">{ACTIONS.map(([a, l]) => <button key={a || "all"} className={action === a ? "on" : ""} onClick={() => setAction(a)}>{l}</button>)}</div>
          <select className={`at-select ${area ? "set" : ""}`} value={area} onChange={(e) => { setArea(e.target.value); setType(""); }} aria-label="Alan">
            <option value="">Tüm alanlar</option>
            {Object.entries(AREA_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className={`at-select ${type ? "set" : ""}`} value={type} onChange={(e) => setType(e.target.value)} aria-label="Kayıt türü">
            <option value="">Tüm kayıt türleri</option>
            {Object.entries(AREA_LABEL).filter(([k]) => !area || k === area).map(([k, l]) => {
              const items = (meta?.types || []).filter((t) => (t.area || "other") === k);
              return items.length ? <optgroup key={k} label={l}>{items.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}</optgroup> : null;
            })}
          </select>
          <select className={`at-select ${actor ? "set" : ""}`} value={actor} onChange={(e) => setActor(e.target.value)} aria-label="Kullanıcı">
            <option value="">Tüm kullanıcılar</option>
            <optgroup label="Kullanıcılar">{(meta?.actors || []).map((a) => <option key={a.userId} value={a.userId}>{a.name || a.email}{a.name ? ` · ${a.email}` : ""}{a.isActive ? "" : " (pasif)"}</option>)}</optgroup>
            <optgroup label="Kullanıcı dışı"><option value="kind:System">Sistem (zamanlanmış işler)</option><option value="kind:Device">Cihaz (kiosk, okuyucu)</option><option value="kind:Anonymous">Ziyaretçi (girişsiz)</option></optgroup>
          </select>
          <button className={`at-chip ${failOnly ? "on danger" : ""}`} onClick={() => setFailOnly(!failOnly)}>{I.warn} Yalnız başarısız</button>
          {filterOn && <button className="at-clear" onClick={clearAll}>{I.filterOff} Temizle</button>}
        </div>

        <div className="at-card-head">
          <span><b>{nf(page?.total ?? 0)}</b> kayıt{cursors.length > 1 && <> · sayfa <b>{cursors.length}</b></>}
            {q && <span className="at-pill">“{q.length > 24 ? `${q.slice(0, 24)}…` : q}”<button aria-label="Aramayı kaldır" onClick={() => { setQInput(""); setQ(""); }}>×</button></span>}
          </span>
          <span><span className="at-hint">{fmtLocal(from)} – {fmtLocal(to)}</span>
            <select className="at-select small" value={size} onChange={(e) => setSize(Number(e.target.value))} aria-label="Sayfa boyu">{[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} satır</option>)}</select>
          </span>
        </div>

        {rows.length === 0 ? (
          <div className="at-blank"><div className="at-blank-icon">{listError ? I.warn : I.shield}</div>
            {listError ? <><b>Liste yüklenemedi</b>{listError}<button className="at-btn" onClick={refresh}>{I.refresh} Yeniden dene</button></> : filterOn ? <><b>Süzgeçle eşleşen kayıt yok</b>Tarih aralığını genişletin ya da süzgeci temizleyin.<button className="at-btn" onClick={clearAll}>{I.filterOff} Süzgeci temizle</button></>
              : <><b>Bu aralıkta denetim kaydı yok</b>Kullanıcılar veri girdikçe değişiklikler burada listelenir.</>}
          </div>
        ) : (
          <div className="at-table-wrap"><table className="at-table">
            <thead><tr><th>Zaman</th><th>Kullanıcı</th><th>İşlem</th><th>Kayıt</th><th className="hide-md">Değişen alanlar</th><th className="hide-lg">Kaynak</th><th className="act" /></tr></thead>
            <tbody onMouseLeave={() => setHoverCid(null)}>{rows.map((r) => (
              <tr key={r.id}
                className={[drawer?.mode === "detail" && drawer.id === r.id ? "on" : "", hoverCid && hoverCid === r.correlationId ? "grp" : "", !r.success ? "fail" : ""].join(" ")}
                onMouseEnter={() => setHoverCid(r.correlationId)} onClick={() => openDetail(r.id)}>
                <td className="when"><div className="at-name num">{fmtLocal(r.occurredAt, true).slice(0, 5)} {r.occurredAt.slice(11, 19)}</div><div className="at-sub">{relTime(r.occurredAt)}</div></td>
                <td><ActorCell r={r} name={actorName(r.actorUserId)} /></td>
                <td><span className={`at-kind ${r.success ? ACTION_CLASS[r.action] : "fail"}`}>{r.action === "Event" ? (r.success ? "Olay" : "Başarısız") : ACTION_LABEL[r.action]}</span></td>
                <td><RecordCell r={r} typeLabel={typeLabel(r.entityType)} /></td>
                <td className="hide-md"><FieldChips r={r} /></td>
                <td className="hide-lg"><div className="at-mono">{r.ip || "—"}</div><div className="at-sub" title={r.request || ""}>{r.request || ""}</div></td>
                <td className="act"><button className="at-ib" title="Ayrıntı" onClick={(e) => { e.stopPropagation(); openDetail(r.id); }}>{I.eye}</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        {(cursors.length > 1 || page?.nextCursor) && (
          <div className="at-pager">
            <span>Sayfa <b>{cursors.length}</b> · en yeni kayıt en üstte</span>
            <div>
              <button className="at-btn small" disabled={cursors.length <= 1 || busy} onClick={() => goPage(-1)}>{I.left} Önceki</button>
              <button className="at-btn small" disabled={!page?.nextCursor || busy} onClick={() => goPage(1)}>Sonraki {I.right}</button>
            </div>
          </div>
        )}
      </section>

      {drawer && (
        <div className="at-overlay" onClick={closeDrawer}>
          <aside className="at-drawer" role="dialog" aria-modal="true" aria-label="Denetim kaydı" onClick={(e) => e.stopPropagation()}>
            {drawer.mode === "detail"
              ? <DetailView key={`d${drawer.id}`} id={drawer.id} canBack={stack.length > 0} onBack={back} onClose={closeDrawer}
                  canStep={currentIdx >= 0} hasPrev={currentIdx > 0} hasNext={currentIdx >= 0 && currentIdx < rows.length - 1} onStep={step}
                  actorName={actorName} typeLabel={typeLabel} typeExists={typeExists}
                  onOpen={(id) => openDetail(id, true)} onHistory={openHistory}
                  onFilterCid={(cid) => { setQInput(cid); setQ(cid); closeDrawer(); }}
                  onFilterActor={(id) => { setActor(id); closeDrawer(); }} />
              : <HistoryView key={`h${drawer.type}:${drawer.id}`} type={drawer.type} id={drawer.id} label={drawer.label} canBack={stack.length > 0} onBack={back} onClose={closeDrawer}
                  actorName={actorName} typeLabel={typeLabel} typeExists={typeExists} onOpen={(id) => openDetail(id, true)} onHistory={openHistory} />}
          </aside>
        </div>
      )}

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert((a) => ({ ...a, isOpen: false }))} />
    </div>
  );
};

// ============================================================================ hücreler

const ActorCell: React.FC<{ r: AuditRow; name: string | null }> = ({ r, name }) => {
  if (r.actorKind !== "User") {
    const icon = r.actorKind === "System" ? I.cpu : r.actorKind === "Device" ? I.device : I.globe;
    return (
      <div className="at-row"><span className="at-avatar sys">{icon}</span>
        <div><div className="at-name"><span className="at-sys">{ACTOR_KIND_LABEL[r.actorKind].toLocaleUpperCase("tr-TR")}</span></div><div className="at-sub">{r.actorEmail || "—"}</div></div>
      </div>
    );
  }
  const label = name || r.actorEmail || "—";
  return (
    <div className="at-row"><span className="at-avatar" style={{ ["--at-hue" as any]: hueOf(r.actorEmail || label) }}>{initials(name || r.actorEmail || "?")}</span>
      <div><div className="at-name">{label}</div><div className="at-sub">{name ? r.actorEmail : r.actorRole || ""}</div></div>
    </div>
  );
};

const RecordCell: React.FC<{ r: AuditRow; typeLabel: string }> = ({ r, typeLabel }) => {
  if (r.action === "Event" || r.eventCode) {
    return (<><div className="at-name">{r.eventLabel || r.eventCode || "Olay"}</div><div className="at-sub" title={r.message || ""}>{r.entityLabel ? `${r.entityLabel} · ` : ""}{r.message || typeLabel}</div></>);
  }
  return (<><div className="at-name" title={r.entityLabel || r.entityId || ""}>{r.entityLabel || (r.entityId ? `#${r.entityId.slice(0, 8)}` : "—")}</div><div className="at-sub">{typeLabel}</div></>);
};

const FieldChips: React.FC<{ r: AuditRow }> = ({ r }) => {
  if (r.action === "Event") return r.eventCode ? <span className="at-mono at-sub">{r.eventCode}</span> : null;
  if (r.action === "Delete") return <span className="at-field del">kayıt silindi</span>;
  if (r.action === "Insert") return <span className="at-field ins">yeni kayıt · {r.changedColumns.length} alan</span>;
  const cols = r.changedColumns;
  return (
    <div className="at-fields">
      {cols.slice(0, 3).map((c) => MASKED_FIELDS.has(c)
        ? <span key={c} className="at-field masked" title="Gizli alan: değeri kaydedilmez">{I.lock}{fieldLabel(c)}</span>
        : <span key={c} className="at-field">{fieldLabel(c)}</span>)}
      {cols.length > 3 && <span className="at-field more" title={cols.slice(3).map(fieldLabel).join(", ")}>+{cols.length - 3}</span>}
    </div>
  );
};

// ============================================================================ değer / fark

interface ValueCtx { field: string; typeExists: (t: string) => boolean; onHistory: (t: string, id: string) => void }

const Value: React.FC<{ v: unknown; ctx: ValueCtx }> = ({ v, ctx }) => {
  const d = describeValue(v);
  if (d.kind === "null") return <span className="null">—</span>;
  if (d.kind === "masked") return <span className="at-masked" title="Gizli alan: değeri hiçbir zaman kaydedilmez; yalnız değiştiği bilinir">{I.lock}{d.text}</span>;
  if (d.kind === "guid") {
    const ref = ctx.field.endsWith("Id") ? ctx.field.slice(0, -2) : "";
    return <span className="guid" title={d.full}>{d.text}{ref && ctx.typeExists(ref) && <> <button type="button" className="at-link at-more" onClick={() => ctx.onHistory(ref, d.full!)}>geçmiş</button></>}</span>;
  }
  if (d.kind === "number") return <span className="num">{d.text}</span>;
  if (d.kind === "text" || d.kind === "json") return <Clamp text={d.text} />;
  return <span>{d.text}</span>;
};

const DiffTable: React.FC<{ changes: Record<string, [unknown, unknown]>; action: AuditActionName; typeExists: (t: string) => boolean; onHistory: (t: string, id: string) => void }> = ({ changes, action, typeExists, onHistory }) => {
  const entries = Object.entries(changes || {});
  if (entries.length === 0) return <div className="at-blank-small">Alan değişikliği yok.</div>;
  const single = action === "Insert" || action === "Delete";
  return (
    <table className={`at-diff ${single ? "single" : ""} stack`}>
      <thead><tr><th>Alan</th>{single ? <th>{action === "Insert" ? "Değer" : "Son değer"}</th> : <><th>Eski değer</th><th>Yeni değer</th></>}</tr></thead>
      <tbody>{entries.map(([k, pair]) => {
        const [o, n] = Array.isArray(pair) ? pair : [undefined, pair];
        const ctx = { field: k, typeExists, onHistory };
        return (
          <tr key={k}>
            <td className="f">{fieldLabel(k)}<small>{k}</small></td>
            {action === "Insert" ? <td className="at-new" data-l="Değer"><Value v={n} ctx={ctx} /></td>
              : action === "Delete" ? <td className="at-old" data-l="Son değer"><span className="v"><Value v={o} ctx={ctx} /></span></td>
                : <><td className="at-old" data-l="Eski"><span className="v"><Value v={o} ctx={ctx} /></span></td><td className="at-new" data-l="Yeni"><Value v={n} ctx={ctx} /></td></>}
          </tr>
        );
      })}</tbody>
    </table>
  );
};

/** Olay verisi: eklenen/kaldırılan izinler kod çipleriyle, neden Türkçe, gerisi anahtar–değer. */
const EventData: React.FC<{ data: any }> = ({ data }) => {
  if (!data || typeof data !== "object") return null;
  if (data._error) return <p className="at-hint">Bu kaydın ayrıntısı yazılamadı (serileştirme hatası).</p>;
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return null;
  const render = (k: string, v: any): React.ReactNode => {
    if (k === "reason" || k === "result") return REASON_LABEL[String(v)] || String(v);
    if ((k === "added" || k === "removed") && Array.isArray(v)) return v.length ? <span className="at-codes">{v.map((c: string, i: number) => <span key={i} className={`at-code ${k === "added" ? "add" : "rem"}`}>{c}</span>)}</span> : <span className="null">—</span>;
    if (Array.isArray(v)) return v.length ? <span className="at-codes">{v.map((c: any, i: number) => <span key={i} className="at-code">{typeof c === "object" ? JSON.stringify(c) : String(c)}</span>)}</span> : <span className="null">—</span>;
    if (v && typeof v === "object") {
      const inner = Object.entries(v).filter(([, x]) => x !== null && x !== undefined && x !== "");
      return inner.length ? <span className="at-codes">{inner.map(([ik, iv]) => <span key={ik} className="at-code">{ik}: {String(iv)}</span>)}</span> : <span className="null">—</span>;
    }
    const d = describeValue(v);
    return d.kind === "null" ? <span className="null">—</span> : d.text;
  };
  return <dl className="at-kv">{entries.map(([k, v]) => <React.Fragment key={k}><dt>{DATA_KEY_LABEL[k] || fieldLabel(k)}</dt><dd>{render(k, v)}</dd></React.Fragment>)}</dl>;
};

// ============================================================================ çekmece: tek kayıt

interface ViewProps {
  actorName: (id?: string | null) => string | null;
  typeLabel: (t: string) => string;
  typeExists: (t: string) => boolean;
  onOpen: (id: number) => void;
  onHistory: (t: string, id: string, label?: string | null) => void;
  canBack: boolean; onBack: () => void; onClose: () => void;
}

const DrawerTools: React.FC<{ onClose: () => void; canStep?: boolean; hasPrev?: boolean; hasNext?: boolean; onStep?: (d: 1 | -1) => void }> = ({ onClose, canStep, hasPrev, hasNext, onStep }) => (
  <div className="at-drawer-tools">
    {canStep && onStep && <>
      <button className="at-ib big hide-sm" title="Önceki satır (←)" disabled={!hasPrev} onClick={() => onStep(-1)}>{I.left}</button>
      <button className="at-ib big hide-sm" title="Sonraki satır (→)" disabled={!hasNext} onClick={() => onStep(1)}>{I.right}</button>
    </>}
    <button className="at-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
  </div>
);

const DetailView: React.FC<ViewProps & {
  id: number; canStep: boolean; hasPrev: boolean; hasNext: boolean; onStep: (d: 1 | -1) => void;
  onFilterCid: (cid: string) => void; onFilterActor: (id: string) => void;
}> = (p) => {
  const [d, setD] = useState<AuditDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [siblings, setSiblings] = useState<AuditDetail[] | null>(null);
  const [showUa, setShowUa] = useState(false);

  useEffect(() => {
    let live = true;
    setD(null); setErr(null); setSiblings(null);
    auditService.get(p.id).then((x) => {
      if (!live) return;
      setD(x);
      if (x.siblingCount > 0) auditService.correlation(x.correlationId).then((s) => live && setSiblings(s.filter((r) => r.id !== x.id))).catch(() => live && setSiblings([]));
      else setSiblings([]);
    }).catch((e) => live && setErr(errorText(e, "Kayıt bulunamadı (saklama süresi dolmuş olabilir).")));
    return () => { live = false; };
  }, [p.id]);

  const head = (
    <div className="at-drawer-head">
      <div className="at-drawer-title">
        {d ? <>
          <span className="at-kicker">{d.action === "Event" ? (d.eventLabel || "Olay") : `${p.typeLabel(d.entityType)} · ${ACTION_LABEL[d.action]}`}</span>
          <h2>{d.action === "Event" ? (d.entityLabel || d.eventLabel || d.eventCode) : (d.entityLabel || (d.entityId ? `#${d.entityId.slice(0, 8)}` : p.typeLabel(d.entityType)))}</h2>
          <div className="at-badges">
            <span className={`at-kind big ${d.success ? ACTION_CLASS[d.action] : "fail"}`}>{d.action === "Event" ? (d.success ? "Olay" : "Başarısız") : ACTION_LABEL[d.action]}</span>
            <span className="at-badge muted">{fmtLocal(d.occurredAt, true)}</span>
            <span className="at-badge info">#{d.id}</span>
            {p.canStep && <span className="at-badge muted hide-sm">← → satırlar arası</span>}
          </div>
        </> : <><span className="at-kicker">Denetim kaydı</span><h2>#{p.id}</h2></>}
      </div>
      <DrawerTools onClose={p.onClose} canStep={p.canStep} hasPrev={p.hasPrev} hasNext={p.hasNext} onStep={p.onStep} />
    </div>
  );

  if (err) return <>{head}<div className="at-drawer-body"><div className="at-blank"><div className="at-blank-icon">{I.info}</div><b>Kayıt açılamadı</b>{err}</div></div></>;
  if (!d) return <>{head}<div className="at-drawer-body"><div className="at-loading"><span className="at-spin" /> Yükleniyor</div></div></>;

  const name = p.actorName(d.actorUserId);
  const target = d.targetUserId && d.targetUserId !== d.actorUserId ? (p.actorName(d.targetUserId) || `silinmiş ya da bilinmeyen hesap · ${d.targetUserId.slice(0, 8)}`) : null;
  const isEntity = d.action !== "Event" && !d.eventCode && d.changes && typeof d.changes === "object" && !Array.isArray(d.changes) && !d.changes._error;
  const nChanges = isEntity ? Object.keys(d.changes).length : 0;
  const ctx = { typeExists: p.typeExists, onHistory: (t: string, id: string) => p.onHistory(t, id) };

  return (<>
    {head}
    <div className="at-drawer-body">
      {p.canBack && <button className="at-link at-back" onClick={p.onBack}>← önceki görünüme dön</button>}

      <dl className="at-facts">
        <div><dt>Kim</dt><dd>{d.actorKind === "User" ? <>{name || d.actorEmail}{name && <small>{d.actorEmail}</small>}{d.actorRole && <span className="at-badge muted">{d.actorRole}</span>}</> : <><span className="at-sys">{ACTOR_KIND_LABEL[d.actorKind].toLocaleUpperCase("tr-TR")}</span>{d.actorEmail}</>}</dd></div>
        <div><dt>Ne zaman</dt><dd>{fmtLocal(d.occurredAt, true)} <small>{relTime(d.occurredAt)}</small></dd></div>
        <div><dt>Nereden</dt><dd className="mono">{d.ip || "—"}{d.request && <small>{d.request}</small>}</dd></div>
        <div><dt>İstek no</dt><dd className="mono">{d.correlationId.slice(0, 12)}…<CopyIdButton value={d.correlationId} label="İstek no" /></dd></div>
        {d.entityId && <div><dt>Kayıt kimliği</dt><dd className="mono">{d.entityId.length > 14 ? `${d.entityId.slice(0, 13)}…` : d.entityId}<CopyIdButton value={d.entityId} label="Kayıt kimliği" /></dd></div>}
        {d.parentType && d.parentId && <div><dt>Üst kayıt</dt><dd>{p.typeLabel(d.parentType)} <span className="at-mono">{d.parentId.slice(0, 8)}</span><button className="at-link" onClick={() => p.onHistory(d.parentType!, d.parentId!)}>geçmiş</button></dd></div>}
        {target && <div><dt>Etkilenen hesap</dt><dd>{target}</dd></div>}
        {d.userAgent && <div className="wide"><dt>Tarayıcı / istemci</dt><dd>{showUa ? <small style={{ fontSize: "0.76rem" }}>{d.userAgent}</small> : <button className="at-link" onClick={() => setShowUa(true)}>göster</button>}</dd></div>}
      </dl>

      {d.action === "Event" || !isEntity ? (
        <section className="at-sec">
          <h3>Olay ayrıntısı</h3>
          {d.message && <p className={`at-summary ${d.success ? "" : "fail"}`}>{d.message}</p>}
          <EventData data={d.changes} />
        </section>
      ) : (
        <section className="at-sec">
          <h3>{d.action === "Insert" ? "Oluşturulan değerler" : d.action === "Delete" ? "Silinen kaydın son hali" : "Değişiklikler"} <em>{nChanges}</em></h3>
          <DiffTable changes={d.changes} action={d.action} {...ctx} />
        </section>
      )}

      <section className="at-sec">
        <h3>Aynı istekteki kayıtlar <em>{d.siblingCount}</em></h3>
        {siblings === null ? <div className="at-loading"><span className="at-spin" /></div>
          : siblings.length === 0 ? <div className="at-blank-small">Bu istekte başka kayıt değişmedi.</div>
            : <div className="at-related">{siblings.map((s) => (
              <button key={s.id} className="at-rel" onClick={() => p.onOpen(s.id)}>
                <span className={`at-kind ${s.success ? ACTION_CLASS[s.action] : "fail"}`}>{s.action === "Event" ? "Olay" : ACTION_LABEL[s.action]}</span>
                <div><b>{s.action === "Event" ? (s.eventLabel || s.eventCode) : (s.entityLabel || `${p.typeLabel(s.entityType)} #${(s.entityId || "").slice(0, 8)}`)}</b>
                  <small>{s.action === "Event" ? (s.message || "") : `${p.typeLabel(s.entityType)} · ${s.changedColumns.length} alan`}</small></div>
                <time>{s.occurredAt.slice(11, 19)}</time>
              </button>
            ))}</div>}
      </section>

      <div className="at-actrow">
        {d.entityId && !d.entityType.startsWith("bulk.") && <button className="at-btn small" onClick={() => p.onHistory(d.entityType, d.entityId!, d.entityLabel)}>{I.shield} Kaydın tüm geçmişi</button>}
        <button className="at-btn small" onClick={() => p.onFilterCid(d.correlationId)}>{I.filterOff} Bu istekle süz</button>
        {d.actorUserId && <button className="at-btn small" onClick={() => p.onFilterActor(d.actorUserId!)}>{I.eye} Bu kullanıcının işlemleri</button>}
      </div>
    </div>
  </>);
};

// ============================================================================ çekmece: kayıt geçmişi

const HistoryView: React.FC<ViewProps & { type: string; id: string; label?: string | null }> = (p) => {
  const [rows, setRows] = useState<AuditDetail[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    setRows(null); setErr(null);
    auditService.entity(p.type, p.id, true).then((r) => { if (live) { setRows(r); if (r.length) setOpen(r[0].id); } })
      .catch((e) => live && setErr(errorText(e, "Kayıt geçmişi yüklenemedi.")));
    return () => { live = false; };
  }, [p.type, p.id]);

  const label = p.label || rows?.find((r) => r.entityType === p.type && r.entityLabel)?.entityLabel || `#${p.id.slice(0, 8)}`;

  return (<>
    <div className="at-drawer-head">
      <div className="at-drawer-title">
        <span className="at-kicker">{p.typeLabel(p.type)} · Kayıt geçmişi</span>
        <h2>{label}</h2>
        <div className="at-badges">{rows && <span className="at-badge info">{rows.length} kayıt</span>}<span className="at-badge muted">alt kayıtlar dahil</span></div>
      </div>
      <DrawerTools onClose={p.onClose} />
    </div>
    <div className="at-drawer-body">
      {p.canBack && <button className="at-link at-back" onClick={p.onBack}>← önceki görünüme dön</button>}
      {err ? <div className="at-blank"><div className="at-blank-icon">{I.info}</div><b>Geçmiş açılamadı</b>{err}</div>
        : rows === null ? <div className="at-loading"><span className="at-spin" /> Yükleniyor</div>
          : rows.length === 0 ? <div className="at-blank"><div className="at-blank-icon">{I.shield}</div><b>Bu kayıt için denetim izi yok</b>Kayıt denetim izi açılmadan önce oluşturulmuş ve o tarihten beri değişmemiş olabilir, ya da kayıtlar saklama süresi dolduğu için silinmiş olabilir.</div>
            : <div className="at-timeline">{rows.map((r) => {
              const isOpen = open === r.id;
              const child = r.entityType !== p.type;
              const name = p.actorName(r.actorUserId) || r.actorEmail || ACTOR_KIND_LABEL[r.actorKind];
              const entity = r.action !== "Event" && !r.eventCode && r.changes && typeof r.changes === "object" && !Array.isArray(r.changes) && !r.changes._error;
              return (
                <div key={r.id} className={`at-tl ${r.success ? ACTION_CLASS[r.action] : "k3"}`}>
                  <button onClick={() => setOpen(isOpen ? null : r.id)} aria-expanded={isOpen}>
                    <b><span className={`at-kind ${r.success ? ACTION_CLASS[r.action] : "fail"}`}>{r.action === "Event" ? (r.eventLabel || "Olay") : ACTION_LABEL[r.action]}</span>
                      {child && <span className="at-badge info">{p.typeLabel(r.entityType)}{r.entityLabel ? ` · ${r.entityLabel}` : ""}</span>}
                      {name}</b>
                    <small>{fmtLocal(r.occurredAt, true)}{r.action === "Event" ? (r.message ? ` · ${r.message}` : "") : r.changedColumns.length ? ` · ${r.changedColumns.length} alan: ${r.changedColumns.slice(0, 4).map(fieldLabel).join(", ")}${r.changedColumns.length > 4 ? "…" : ""}` : ""}</small>
                  </button>
                  {isOpen && (entity
                    ? <DiffTable changes={r.changes} action={r.action} typeExists={p.typeExists} onHistory={(t, id) => p.onHistory(t, id)} />
                    : <div style={{ marginTop: "0.5rem" }}><EventData data={r.changes} /></div>)}
                  {isOpen && <div style={{ marginTop: "0.4rem" }}><button className="at-link" onClick={() => p.onOpen(r.id)}>tüm ayrıntı →</button></div>}
                </div>
              );
            })}</div>}
    </div>
  </>);
};

export default AuditTrail;
